import { useRef, useState } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { CITY_MENU } from '@/data/content';

const reveal = {
  hidden: { opacity: 0, y: 32 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } }
};

export default function UniverseSection() {
  const ref = useRef(null);
  const [hovered, setHovered] = useState(CITY_MENU[0]);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'center center'] });
  const scale = useTransform(scrollYProgress, [0, 1], [1.12, 1]);
  const y = useTransform(scrollYProgress, [0, 1], [40, 0]);

  return (
    <section
      id="universe"
      ref={ref}
      data-testid="universe-section"
      className="relative overflow-hidden py-24 sm:py-32"
      aria-label="Welcome to the Toonverse"
    >
      <div className="absolute inset-0 holo-grid opacity-60" aria-hidden="true" />
      <div
        className="absolute inset-0"
        style={{ background: 'radial-gradient(70% 60% at 50% 0%, rgba(22,119,255,0.14) 0%, transparent 70%)' }}
        aria-hidden="true"
      />

      <div className="relative mx-auto max-w-[1400px] px-6 sm:px-10">
        <motion.div variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.3 }}>
          <p className="font-mono text-[10px] uppercase tracking-[0.4em] text-electric">01 — THE UNIVERSE</p>
          <h2
            data-testid="universe-title"
            className="mt-4 font-display text-3xl font-extrabold uppercase tracking-tight sm:text-4xl lg:text-5xl"
          >
            WELCOME TO THE{' '}
            <span className="bg-gradient-to-r from-arc to-electric bg-clip-text text-transparent text-glow-blue">
              TOONVERSE
            </span>
          </h2>
          <p className="mt-5 max-w-2xl text-sm leading-relaxed text-snow/70 sm:text-base" data-testid="universe-copy">
            ARC TOONS is more than a collection of characters. It is a growing cartoon universe where every
            Toon has a personality, a role and a story.
          </p>
        </motion.div>

        <div className="mt-14 grid items-start gap-10 lg:grid-cols-[1.25fr_0.75fr]">
          <motion.div
            variants={reveal}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.25 }}
            className="relative"
          >
            <div
              className="relative overflow-hidden rounded-3xl border border-electric/25 shadow-[0_0_60px_rgba(0,200,255,0.18)]"
              data-testid="universe-banner-frame"
            >
              <motion.img
                src="/assets/arctoons-banner.webp"
                alt="ARC TOONS cinematic banner — The cartoon universe of Arc, one chain, many Toons, infinite stories"
                loading="lazy"
                className="w-full object-cover"
                style={{ scale, y }}
              />
              <div
                className="absolute inset-0"
                style={{ background: 'linear-gradient(to top, rgba(5,11,24,0.55), transparent 40%, rgba(5,11,24,0.15))' }}
                aria-hidden="true"
              />
              <motion.div
                aria-hidden="true"
                className="absolute left-0 h-0.5 w-1/3 bg-gradient-to-r from-transparent via-electric to-transparent"
                animate={{ top: ['15%', '85%', '15%'] }}
                transition={{ duration: 5, repeat: Infinity, ease: 'linear' }}
              />
              <div className="absolute left-5 top-5 rounded-lg border border-electric/30 bg-navy/70 px-3 py-1.5 font-mono text-[9px] uppercase tracking-[0.3em] text-electric">
                TOON CITY // LIVE
              </div>
              <div className="absolute bottom-5 right-5 rounded-lg border border-gold/30 bg-navy/70 px-3 py-1.5 font-mono text-[9px] uppercase tracking-[0.3em] text-gold">
                POPULATION: ∞ TOONS
              </div>
            </div>
          </motion.div>

          <motion.div
            variants={reveal}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.25 }}
            className="rounded-3xl border border-electric/20 bg-navy2/70 p-6 sm:p-8"
            style={{ backdropFilter: 'blur(12px)' }}
            data-testid="city-menu-panel"
          >
            <div className="mb-6 flex items-center justify-between">
              <span className="font-display text-sm font-bold uppercase tracking-[0.25em] text-snow">TOON CITY</span>
              <span className="flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-[0.3em] text-electric">
                <span className="h-1.5 w-1.5 animate-blink rounded-full bg-toongreen" /> ONLINE
              </span>
            </div>
            <ul className="flex flex-col">
              {CITY_MENU.map((item, i) => (
                <li key={item.id}>
                  <button
                    data-testid={`city-menu-${item.id}`}
                    onMouseEnter={() => setHovered(item)}
                    onFocus={() => setHovered(item)}
                    className={`group relative flex w-full items-center justify-between border-b border-electric/10 py-4 text-left transition-all duration-300 ${
                      hovered.id === item.id ? 'pl-3' : ''
                    }`}
                  >
                    <span className="flex items-baseline gap-4">
                      <span className="font-mono text-[10px] text-gold/80">0{i + 1}</span>
                      <span
                        className={`font-display text-xl font-bold uppercase tracking-tight transition-colors duration-300 sm:text-2xl ${
                          hovered.id === item.id ? 'text-electric text-glow-blue' : 'text-snow/80'
                        }`}
                      >
                        {item.label}
                      </span>
                    </span>
                    <span
                      aria-hidden="true"
                      className={`font-mono text-electric transition-all duration-300 ${
                        hovered.id === item.id ? 'translate-x-0 opacity-100' : '-translate-x-2 opacity-0'
                      }`}
                    >
                      →
                    </span>
                    <span
                      aria-hidden="true"
                      className="absolute bottom-0 left-0 h-px w-full origin-left scale-x-0 bg-gradient-to-r from-arc via-electric to-transparent transition-transform duration-500 group-hover:scale-x-100"
                    />
                  </button>
                </li>
              ))}
            </ul>
            <motion.p
              key={hovered.id}
              data-testid="city-menu-description"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="mt-5 min-h-[48px] text-xs leading-relaxed text-snow/60 sm:text-sm"
            >
              {hovered.desc}
            </motion.p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
